import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
// Import Descriptions for displaying the checklist results
import { List, Button, Modal, Form, Input, message, Spin, Empty, Popconfirm, Space, Alert, Typography, Tooltip, Card, Descriptions } from 'antd';
import MomentDatePicker from './MomentDatePicker.tsx';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, HistoryOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import StructuredMedicalHistoryForm from './StructuredMedicalHistoryForm';

const { TextArea } = Input;
const { Text, Title } = Typography; // Import Title for consistency

// Helper to format date
const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('MM/DD/YYYY') : 'N/A';
};

// Helper to render system titles (can be shared or duplicated from the form)
const formatSystemName = (systemKey) => {
    if (!systemKey) return '';

    // --- Add specific Vietnamese names ---
    if (systemKey === 'neurological') return 'THẦN KINH';
    if (systemKey === 'cardiovascular') return 'TUẦN HOÀN';
    if (systemKey === 'respiratory') return 'HÔ HẤP';
    if (systemKey === 'renal') return 'THẬN NIỆU - LỌC MÁU';
    if (systemKey === 'metabolic') return 'CHUYỂN HÓA - TIÊU HÓA';
    if (systemKey === 'musculoskeletal') return 'CƠ XƯƠNG KHỚP';
    if (systemKey === 'immune') return 'MIỄN DỊCH - DỊ ỨNG - VIÊM';
    if (systemKey === 'womensHealth') return 'NỮ KHOA';
    if (systemKey === 'mensHealth') return 'NAM KHOA';
    if (systemKey === 'cancer') return 'UNG THƯ (đã có hoặc đang điều trị)';
    // --- End specific names ---

    // Exclude internal fields from display title logic
    if (['lastUpdatedAt', 'lastUpdatedBy', '_id'].includes(systemKey)) return null;

    // Default formatting for other keys (e.g., 'other')
    // Capitalize first letter and add space before caps for any remaining keys
    return systemKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};


const MedicalHistorySection = ({ patientId }) => {
  const [history, setHistory] = useState([]); // For free-text items
  const [lastReviewed, setLastReviewed] = useState(null);
  // NEW: State for structured checklist data
  const [structuredHistoryData, setStructuredHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal states (unchanged)
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isChecklistModalVisible, setIsChecklistModalVisible] = useState(false);

  const { user } = useAuth();

  // Fetch data including structured history
  const fetchHistory = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch patient data populating review user
      const response = await axios.get(`/patients/${patientId}?populate=medicalHistoryLastReviewed.user`);
      const patientData = response.data.data.patient;
      setHistory(patientData?.medicalHistory || []);
      setLastReviewed(patientData?.medicalHistoryLastReviewed || null);
      // Set structured history state
      setStructuredHistoryData(patientData?.structuredMedicalHistory || null);
    } catch (err) {
      console.error("Error fetching medical history data:", err);
      setError(err.response?.data?.message || 'Failed to fetch medical history data.');
      setHistory([]);
      setLastReviewed(null);
      setStructuredHistoryData(null); // Clear on error
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // --- Handlers for Original Item Modal (Add/Edit Free Text) --- unchanged ---
  const showItemModal = (item = null) => {
    setEditingItem(item);
    if (item) {
      const currentItem = history.find(h => h.condition === item.condition && h.diagnosisDate === item.diagnosisDate);
      form.setFieldsValue({
        ...currentItem,
        diagnosisDate: currentItem?.diagnosisDate ? moment(currentItem.diagnosisDate) : null,
      });
    } else {
      form.resetFields();
    }
    setIsItemModalVisible(true);
  };

  const handleItemModalCancel = () => {
    setIsItemModalVisible(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleItemFinish = async (values) => {
    setSubmitLoading(true);
    setError(null);
    const formattedValues = { ...values, diagnosisDate: values.diagnosisDate ? values.diagnosisDate.toISOString() : null };
    let updatedHistory;
    if (editingItem) {
      const index = history.findIndex(item => item.condition === editingItem.condition && item.diagnosisDate === editingItem.diagnosisDate);
      if (index > -1) { updatedHistory = [...history]; updatedHistory[index] = formattedValues; }
      else { message.error("Could not find original item. Please refresh."); setSubmitLoading(false); return; }
    } else { updatedHistory = [...history, formattedValues]; }

    try {
      await axios.patch(`/patients/${patientId}`, { medicalHistory: updatedHistory });
      message.success(`History item ${editingItem ? 'updated' : 'added'} successfully!`);
      handleItemModalCancel();
      setHistory(updatedHistory); // Update local state
    } catch (err) {
      console.error("Error saving history item:", err);
      message.error(err.response?.data?.message || 'Failed to save history item.');
    } finally { setSubmitLoading(false); }
  };

  const handleItemDelete = async (itemToDelete) => {
     const updatedHistory = history.filter(item => !(item.condition === itemToDelete.condition && item.diagnosisDate === itemToDelete.diagnosisDate));
     try {
        setLoading(true);
        await axios.patch(`/patients/${patientId}`, { medicalHistory: updatedHistory });
        message.success('History item deleted!');
        setHistory(updatedHistory);
     } catch (err) { console.error("Error deleting history item:", err); message.error(err.response?.data?.message || 'Failed to delete item.');
     } finally { setLoading(false); }
  };
  // --- End Original Item Modal Handlers ---

  // --- Handlers for NEW Structured Checklist Modal ---
  const showChecklistModal = () => { setIsChecklistModalVisible(true); };
  const handleChecklistModalCancel = () => { setIsChecklistModalVisible(false); };

  const handleChecklistSaveSuccess = () => {
      setIsChecklistModalVisible(false);
      message.success("Checklist saved successfully!"); // Give feedback immediately
      // Refetch all history data to update the display
      fetchHistory();
  };


  // Handle "Mark as Reviewed" - unchanged
   const handleMarkReviewed = async () => {
     if (!user || !user._id) { message.error("User not logged in."); return; }
     const reviewData = { user: user._id, date: new Date().toISOString() };
     try {
       setLoading(true);
       await axios.patch(`/patients/${patientId}`, { medicalHistoryLastReviewed: reviewData });
       message.success('Medical History marked as reviewed.');
       setLastReviewed({ user: { _id: user._id, name: user.name }, date: reviewData.date });
     } catch (err) { console.error("Error marking as reviewed:", err); message.error(err.response?.data?.message || 'Failed to mark.');
     } finally { setLoading(false); }
   };

  const canModify = user && ['Admin', 'MD', 'Nursing'].includes(user.role);

  // Helper function to check if any checklist items are selected
  const hasSelectedChecklistItems = (data) => {
      if (!data) return false;
      // Check if any system array has length > 0, excluding internal fields
      return Object.entries(data).some(([key, value]) =>
          !['lastUpdatedAt', 'lastUpdatedBy', '_id'].includes(key) && Array.isArray(value) && value.length > 0
      );
  };

  return (
    <Spin spinning={loading}>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: '10px' }}>
        {/* Action Buttons - unchanged */}
        <Space>
           {canModify && (<Button type="primary" icon={<HistoryOutlined />} onClick={showChecklistModal}>Update Medical History Checklist</Button>)}
           {canModify && (<Button icon={<PlusOutlined />} onClick={() => showItemModal(null)} > Add History Item (Note) </Button>)}
           {canModify && (<Tooltip title="Mark this section as reviewed by you right now"><Button icon={<CheckCircleOutlined />} onClick={handleMarkReviewed}> Mark as Reviewed </Button></Tooltip>)}
        </Space>
         {/* Display Last Reviewed Status - unchanged */}
         <div style={{ textAlign: 'right' }}>
             {lastReviewed?.date ? (<Text type="secondary" style={{ fontSize: '0.85em' }}> Last Reviewed by {lastReviewed.user?.name || 'Unknown User'} on {moment(lastReviewed.date).format('L LT')} </Text>
             ) : ( <Text type="secondary" style={{ fontSize: '0.85em' }}>Not yet reviewed</Text> )}
         </div>
      </div>

      {/* --- NEW: Display Structured Checklist Items --- */}
       <Card size="small" title="Medical History Checklist Summary" style={{ marginBottom: 16, background: '#fafafa' }}>
            {structuredHistoryData && hasSelectedChecklistItems(structuredHistoryData) ? (
                 Object.entries(structuredHistoryData)
                    .filter(([key, value]) => !['lastUpdatedAt', 'lastUpdatedBy', '_id'].includes(key) && Array.isArray(value) && value.length > 0) // Filter out empty/internal fields
                    .map(([systemKey, diagnoses]) => {
                        const systemName = formatSystemName(systemKey);
                        return systemName ? ( // Render only if systemName is valid
                            <Descriptions key={systemKey} title={<Text strong style={{ fontSize: '1em', display: 'block', marginBottom: '4px' }}>{systemName}</Text>} size="small" column={1} style={{ marginBottom: 10 }}>
                                <Descriptions.Item>
                                    <List
                                        size="small"
                                        dataSource={diagnoses}
                                        renderItem={item => (
                                            <List.Item style={{ borderBottom: 'none', padding: '0' }}> {/* Remove default list item styling */}
                                                <span className="history-tag">{item}</span>
                                            </List.Item>
                                        )}
                                        grid={{ gutter: 0, column: 1 }} // Use grid to allow items to flow naturally if needed, but keep single column for now
                                    />
                                </Descriptions.Item>
                            </Descriptions>
                        ) : null; // Don't render internal fields like lastUpdatedBy
                    })
            ) : (
                 <Empty description="No checklist items selected." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
             {/* Optionally display last updated time for the checklist */}
             {structuredHistoryData?.lastUpdatedAt && (
                 <div style={{ textAlign: 'right', marginTop: '10px' }}>
                     <Text type="secondary" style={{ fontSize: '0.85em' }}>
                         Checklist Last Updated: {moment(structuredHistoryData.lastUpdatedAt).format('L LT')}
                         {/* Add user name if available and needed */}
                         {/* {structuredHistoryData.lastUpdatedBy ? ` by ${structuredHistoryData.lastUpdatedBy.name}` : ''} */}
                     </Text>
                 </div>
             )}
       </Card>


      {/* List for the original free-text medical history items - unchanged */}
      <Title level={5} style={{ marginTop: '20px', marginBottom: '10px' }}>Medical History Notes</Title>
      <List
        itemLayout="horizontal"
        dataSource={history}
        locale={{ emptyText: <Empty description="No specific medical history items/notes recorded." /> }}
        renderItem={item => (
          <List.Item
            actions={canModify ? [
              <Button icon={<EditOutlined />} onClick={() => showItemModal(item)} size="small" key={`edit-${item.condition}`} />,
              <Popconfirm key={`delete-${item.condition}`} title="Delete this item?" onConfirm={() => handleItemDelete(item)} okText="Yes" cancelText="No" >
                <Button icon={<DeleteOutlined />} danger size="small" />
              </Popconfirm>
            ] : []}
          >
            <List.Item.Meta
              title={item.condition}
              description={ <Space direction="vertical" size="small"> {item.diagnosisDate && <Text type="secondary">Onset/Dx Date: {formatDate(item.diagnosisDate)}</Text>} {item.notes && <Text>Notes: {item.notes}</Text>} </Space> }
            />
          </List.Item>
        )}
      />

      {/* Original Modal for Adding/Editing free-text items - unchanged */}
      <Modal
          title={editingItem ? 'Edit History Item Details' : 'Add History Item (Note)'} // Renamed title
          visible={isItemModalVisible}
          onCancel={handleItemModalCancel}
          footer={null}
          destroyOnClose
      >
          <Spin spinning={submitLoading}>
            <Form form={form} layout="vertical" onFinish={handleItemFinish}>
              <Form.Item name="condition" label="Condition/Diagnosis" rules={[{ required: true, message: 'Condition is required.' }]}>
                  <Input />
              </Form.Item>
              <Form.Item name="diagnosisDate" label="Approximate Diagnosis Date">
                  <MomentDatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="notes" label="Notes">
                  <TextArea rows={3} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={submitLoading}>{editingItem ? 'Save Changes' : 'Add Item'}</Button>
                <Button style={{ marginLeft: 8 }} onClick={handleItemModalCancel}>Cancel</Button>
              </Form.Item>
            </Form>
          </Spin>
      </Modal>

      {/* NEW Modal for the Structured Checklist - unchanged */}
      <Modal
          title="Update Structured Medical History Checklist"
          visible={isChecklistModalVisible}
          onCancel={handleChecklistModalCancel}
          footer={null} // The form has its own buttons
          width={800} // Make modal wider for checklist
          destroyOnClose // Destroy form state when modal closes
      >
          {isChecklistModalVisible && patientId && (
              <StructuredMedicalHistoryForm
                  patientId={patientId}
                  onSaveSuccess={handleChecklistSaveSuccess}
                  onCancel={handleChecklistModalCancel}
              />
          )}
      </Modal>

    </Spin>
  );
};


export default MedicalHistorySection;
